/**
 * 열린 입찰 — 이 기사가 신청할 수 있는 배차. 선착순 1건.
 *
 * 신청에는 편성(car_alloc)이 필요하다. 편성이 하나면 그걸로 바로, 여럿이면 차량을 고르게 한다.
 * 선착순은 서버 DB 제약이 강제한다 — 밀리면 "이미 다른 기사가 신청" 문구가 온다.
 *
 * #391(10-23) 신청은 확인 창을 한 번 거친다 · 기사는 앱에서 취소하지 못한다 — 접수 담당자에게 전화해 승인을 받으면
 * 담당자가 사무실 화면(접수·배차 › 입찰)에서 취소한다. 확인 창과 신청 완료 카드에 담당자 연락처를 보인다.
 * #386 이미 신청·배정된 노선은 「신청 완료」·「배정 완료」로만 보인다(서버도 다시 신청을 막는다). #392 남은 대수.
 * 10-26 입찰 알림을 누르면 `?dispt=` 로 들어온다 — 그 카드로 스크롤해 테두리로 강조한다(목록은 새로 받은 뒤 찾는다 —
 * 받아 둔 목록엔 방금 공개된 입찰이 아직 없을 수 있다). 목록에 없으면(마감·조건 밖) 그렇다고 알린다.
 * 접수 담당 연락처 = DYOPS 사용자 정보의 휴대폰(동기화) → 없으면 버스인 계정 번호 — 카드에서 바로 전화.
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDriverAuth } from '../lib/driverAuth';
import { driverApi, driverError, type BidAlloc, type OpenBid } from '../lib/driverApi';
import { refreshDriverList, useDriverList } from '../lib/driverCache';
import Screen, { BidTabs, Empty, ErrorBanner, Loading } from './Screen';
import { when, whenArrive } from './format';

export default function Bids() {
  const { allocs: myAllocs } = useDriverAuth();
  // 홈에서 미리 받아 둔 목록을 바로 그리고 뒤에서 새로 받는다(lib/driverCache.ts).
  // 받아 둔 사이 남이 먼저 신청했으면 신청 때 서버가 「이미 다른 기사가 신청」으로 막는다(선착순은 DB 제약).
  const list = useDriverList('bids', '입찰 목록을 불러오지 못했습니다.');
  const rows: OpenBid[] | null = list.data?.rows ?? null;
  // 고를 차량 — 서버 목록(관리기사B 차주면 자기 차량 전부 · 10-30 way). 옛 응답이면 본인 편성.
  const allocs: BidAlloc[] = list.data?.bidAllocs ?? myAllocs.map((a) => ({ ...a, driver_name: null, other_driver: false }));
  // 차주가 여러 대를 관리하면 한 대 배정 뒤에도 남은 대수에 다른 차로 더 신청할 수 있다.
  const fleetOwner = allocs.some((a) => a.other_driver);
  const [actionError, setError] = useState<string | null>(null);
  const error = actionError ?? list.error;
  const [busyId, setBusyId] = useState<number | null>(null);
  /** 즉시배차 = 신청하는 순간 배정(10-23) — 끝났다는 걸 화면에 남긴다. */
  const [done, setDone] = useState<string | null>(null);
  const [pickFor, setPickFor] = useState<number | null>(null);
  /** #391 신청 확인 창 — 누른 노선과 고른 편성. */
  const [confirm, setConfirm] = useState<{ bid: OpenBid; carAllocId: number } | null>(null);

  // 알림으로 들어온 입찰 — 새로 받은 목록에서 찾는다.
  const [params] = useSearchParams();
  const focusId = Number(params.get('dispt')) || null;
  const [focusChecked, setFocusChecked] = useState(false);
  const focusRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (!focusId) return;
    setFocusChecked(false);
    void list.reload().finally(() => setFocusChecked(true));
    // list.reload 는 key 가 같으면 같은 함수다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId]);
  const focusFound = !!focusId && !!rows?.some((b) => b.dispt_id === focusId);
  useEffect(() => {
    if (focusChecked && focusFound) focusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusChecked, focusFound]);

  /** 신청 뒤 — 이 목록과 「내 신청 결과」 둘 다 새로 받는다(내 신청 쪽 캐시가 옛 상태를 보이지 않게). */
  async function load() {
    refreshDriverList('myBids');
    await list.reload();
  }

  async function apply(disptId: number, carAllocId: number) {
    setBusyId(disptId);
    setError(null);
    setDone(null);
    try {
      const r = await driverApi.applyBid({ disptId, carAllocId });
      setDone(r.assigned ? '배정되었습니다. 「내 배차」에서 확인하실 수 있습니다.' : '신청했습니다. 사무실에서 확정하면 「내 신청 결과」에 보입니다.');
      setPickFor(null);
      await load();
    } catch (e) {
      setError(driverError(e, '신청하지 못했습니다.'));
    } finally {
      setBusyId(null);
    }
  }

  function onApplyClick(b: OpenBid) {
    if (allocs.length === 0) {
      setError('신청할 수 있는 운행차량편성이 없습니다. 회사에 문의해 주세요.');
      return;
    }
    if (allocs.length === 1 && allocs[0]) {
      setConfirm({ bid: b, carAllocId: allocs[0].car_alloc_id });
    } else {
      setPickFor(pickFor === b.dispt_id ? null : b.dispt_id);
    }
  }

  const pickedCar = confirm ? allocs.find((x) => x.car_alloc_id === confirm.carAllocId) : undefined;

  return (
    <Screen title="입찰 배차">
      <BidTabs active="bids" />
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {done && !error && <div className="driver-card border-emerald-300 bg-emerald-50 px-4 py-3 text-emerald-800">{done}</div>}
      {focusId && focusChecked && !focusFound && rows && (
        <div className="driver-card border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
          알림 받은 입찰은 이미 마감되었거나 지금 신청할 수 없습니다. 아래 열린 입찰을 확인해 주세요.
        </div>
      )}
      {rows === null ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>지금 신청할 수 있는 입찰 배차가 없습니다.</Empty>
      ) : (
        <ul className="space-y-4">
          {rows.map((b) => (
            <li
              key={b.dispt_id}
              ref={b.dispt_id === focusId ? focusRef : undefined}
              className={`driver-card p-5 ${b.dispt_id === focusId ? 'ring-4 ring-brand-500' : ''}`}
            >
              {b.dispt_id === focusId && <p className="mb-2 text-sm font-bold text-brand-700">알림 받은 입찰</p>}
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-lg font-bold text-ink">{b.cust_name || b.dispt_name || '배차'}</h2>
                <span className="shrink-0 rounded-lg bg-brand-50 px-2 py-1 text-sm text-brand-700">즉시</span>
              </div>

              <dl className="mt-3 space-y-2 text-base">
                <Times b={b} />
                <Row k="출발지" v={b.start_loc || '-'} />
                <Row k="행선지" v={b.end_loc || '-'} />
                {b.car_cnt ? <Row k="남은 대수" v={left(b)} /> : null}
                {Number(b.cost_driver ?? 0) > 0 && <Row k="입찰 금액" v={`${Math.round(Number(b.cost_driver)).toLocaleString('ko-KR')}원 (1대)`} />}
                <Row k="마감" v={when(b.close_date)} />
                {b.owner_name ? <OwnerRow b={b} /> : null}
                {b.request ? <Row k="참고사항" v={b.request} /> : null}
              </dl>

              {b.mine_assigned && !(fleetOwner && !b.bid_full) ? (
                <div className="mt-4 w-full rounded-xl bg-emerald-50 py-3 text-center text-lg font-semibold text-emerald-700">배정 완료</div>
              ) : b.bid_full ? (
                <div className="mt-4 w-full rounded-xl bg-canvas py-3 text-center text-lg font-semibold text-ink-soft">
                  입찰 대수({b.car_cnt ?? 0}대)가 다 차서 마감되었습니다
                </div>
              ) : b.mine_pending ? (
                <>
                  <div className="mt-4 w-full rounded-xl bg-brand-50 py-3 text-center text-lg font-semibold text-brand-700">신청 완료</div>
                  <CancelNotice b={b} />
                </>
              ) : (
                <button
                  className="driver-btn mt-4"
                  disabled={busyId === b.dispt_id}
                  onClick={() => onApplyClick(b)}
                >
                  {busyId === b.dispt_id ? '신청 중…' : '신청하기'}
                </button>
              )}

              {pickFor === b.dispt_id && allocs.length > 1 && !b.mine_pending && (!b.mine_assigned || fleetOwner) && (
                <div className="mt-3 space-y-2">
                  <p className="text-base font-semibold text-ink">어느 차량으로 신청할까요?</p>
                  {allocs.map((a) => (
                    <button
                      key={a.car_alloc_id}
                      className="min-h-touch w-full rounded-xl border-2 border-line bg-surface px-4 text-left text-base font-semibold text-ink"
                      disabled={busyId === b.dispt_id}
                      onClick={() => setConfirm({ bid: b, carAllocId: a.car_alloc_id })}
                    >
                      {a.car_no || a.car_name || `편성 ${a.car_alloc_id}`}
                      {a.other_driver && a.driver_name ? <span className="ml-2 text-ink-soft">{a.driver_name} 기사</span> : null}
                      {a.grp_name ? <span className="ml-2 text-ink-soft">{a.grp_name}</span> : null}
                    </button>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center" role="dialog" aria-modal="true">
          <div className="driver-card w-full max-w-md p-5">
            <h2 className="text-xl font-bold text-ink">이 배차에 신청할까요?</h2>
            <dl className="mt-3 space-y-2 text-base">
              <Row k="배차" v={confirm.bid.cust_name || confirm.bid.dispt_name || '배차'} />
              <Times b={confirm.bid} />
              {confirm.bid.car_cnt ? <Row k="남은 대수" v={left(confirm.bid)} /> : null}
              {Number(confirm.bid.cost_driver ?? 0) > 0 && <Row k="입찰 금액" v={`${Math.round(Number(confirm.bid.cost_driver)).toLocaleString('ko-KR')}원 (1대)`} />}
              {allocs.length > 1 && <Row k="차량" v={(pickedCar?.car_no || pickedCar?.car_name || '-') + (pickedCar?.other_driver && pickedCar.driver_name ? ` (${pickedCar.driver_name} 기사)` : '')} />}
            </dl>
            <p className="mt-3 text-base font-semibold text-brand-700">신청하면 바로 배정됩니다.</p>
            <CancelNotice b={confirm.bid} />
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                className="min-h-touch rounded-xl border-2 border-line bg-surface text-lg font-semibold text-ink-soft"
                onClick={() => setConfirm(null)}
              >
                닫기
              </button>
              <button
                className="driver-btn"
                disabled={busyId !== null}
                onClick={() => {
                  const c = confirm;
                  setConfirm(null);
                  void apply(c.bid.dispt_id, c.carAllocId);
                }}
              >
                신청
              </button>
            </div>
          </div>
        </div>
      )}
    </Screen>
  );
}

/** #409 운행 시각 — 출발·도착 + 왕복이면 복귀 출발·복귀 도착. 값 없는 칸은 안 그린다(출발은 늘). */
function Times({ b }: { b: OpenBid }) {
  const shuttle = b.trip_type === '왕복';
  // 레거시 자리값(도착 00:00 이 출발보다 앞)은 거짓 시각이라 안 보인다.
  const ok = (t: string | null | undefined) => !!t && (!b.start_time || t >= b.start_time);
  return (
    <>
      <Row k="출발" v={when(b.start_time)} />
      {ok(b.end_time) ? <Row k="도착" v={whenArrive(b.end_time)} /> : null}
      {shuttle && ok(b.end_dept_time) ? <Row k="복귀 출발" v={whenArrive(b.end_dept_time)} /> : null}
      {shuttle && ok(b.start_arrv_time) ? <Row k="복귀 도착" v={whenArrive(b.start_arrv_time)} /> : null}
    </>
  );
}

/** #392 남은 대수 = 필요 대수 − 배정된 수. */
function left(b: OpenBid): string {
  const need = Number(b.car_cnt ?? 0);
  const rest = Math.max(0, need - Number(b.assigned_cnt ?? 0));
  return `${rest}대 (필요 ${need}대)`;
}

/** #391 취소 안내 — 앱에서 바로 취소되지 않는다. 접수 담당자에게 전화해 승인을 받아야 한다. */
function CancelNotice({ b }: { b: OpenBid }) {
  const tel = (b.owner_phone ?? '').replace(/[^0-9]/g, '');
  return (
    <div className="mt-3 rounded-xl bg-amber-50 p-4 text-base text-amber-900">
      <p>
        신청한 뒤에는 앱에서 바로 취소되지 않습니다. 취소하려면 <b>반드시 버스인접수담당자에게 전화해 승인</b>을 받아 주세요.
      </p>
      {(b.owner_name || tel) && (
        <p className="mt-2 font-semibold">
          버스인접수담당자 {b.owner_name ?? ''}
          {tel ? (
            <a className="ml-2 underline" href={`tel:${tel}`}>
              {b.owner_phone}
            </a>
          ) : null}
        </p>
      )}
    </div>
  );
}

/** 접수 담당 — 이름 + 번호가 있으면 누르면 바로 전화(10-26 way). */
function OwnerRow({ b }: { b: OpenBid }) {
  const tel = (b.owner_phone ?? '').replace(/[^0-9]/g, '');
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="shrink-0 text-ink-soft">버스인접수담당자</dt>
      {/* 10-28 way(글씨 키운 폰) — 이름·전화 버튼이 글자 단위로 꺾여 두 줄이 됐다 → 각자 한 줄(nowrap) · 자리가 모자라면 버튼이 통째로 아랫줄 */}
      <dd className="flex min-w-0 flex-wrap items-center justify-end gap-x-2 gap-y-1 text-right font-medium text-ink">
        <span className="whitespace-nowrap">{b.owner_name}</span>
        {tel ? (
          <a
            href={`tel:${tel}`}
            className="whitespace-nowrap rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white"
            aria-label={`${b.owner_name} 전화하기`}
          >
            전화 {b.owner_phone}
          </a>
        ) : null}
      </dd>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-ink-soft">{k}</dt>
      <dd className="text-right font-medium text-ink">{v}</dd>
    </div>
  );
}
